import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { TiMetricsActionEventDetail } from '@ticom/web-component-utils';
import { default as languageStrings } from '../../global/l10n/OpnDetails.json';
import Util from '../../global/util';
import OPNDao, { OPNDaoConfig } from '../opn-snapshot/opn-snapshot.dao';
import { OPNParameters, Price } from '../opn-snapshot/opn-snapshot.interface';
import OPNDetailsDao from './opn-details.dao';
import { OPNDetail } from './opn-details.interface';


@Component({
    tag: 'ti-opn-details',
    styleUrl: 'opn-details.scss',
    shadow: true
})
export class OpnDetails {

    private _loginUrl = '/secure-link-forward/?gotoUrl=';

    /**
     * EN product statuses to be used in add-to-cart
     */
    private static readonly _PRODUCT_STATUS_EN = {
        1: "ACTIVE",
        5: "PREVIEW",
        4: "OBSOLETE",
        8: "LAST TIME BUY",
        3: "NOT RECOMMENDED FOR NEW DESIGNS"
    }

    /**
     * Load Statuses
     */
    private _statusType = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    }

    /**
     * The element for this component
     */
    @Element() _hostElement: HTMLTiOpnSnapshotElement;

    /**
     * global utility function to format price
     *
     */
    @State() currencyFormat: any = { convertAndFormat: price => price };

    /**
     * Holds opn response data
     *
     * @type {OPNDetails}
     */
    @State() opnDetails: OPNDetail;

    /**
     * Store url
     *
     * @type {string}
     */
    @State() storeUrl: string;

    /**
     * Keep track of the status of the component so that we can render a response to the user accordingly.
     *
     * @type {string}
     * @default StatusType.Loading
     */
    @State() status: string = this._statusType.LOADING;
    @Watch('status')
    handleComponentStatusUpdate(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue === this._statusType.READY) {
            this.componentRendered.emit({
                tag: this._hostElement.tagName
            });
        }
    }

    /**
     * For analytics purposes. Query parameters to add to all outgoing links
     *
     * @type {string}
     * @default 'keyMatch={searchTerm}&tisearch={tisearch}&usecase={usecase}'
     */
    @Prop() commonQueryString: string = 'keyMatch={searchTerm}&tisearch={tisearch}&usecase={usecase}';

    /**
     * Url for fetching custom reel fees
     *
     * @type {string}
     * @default '/search/customReelFees'
     */
    @Prop() customReelFeesApiUrl: string = '/search/customReelFees';

    /**
     * Value for tisearch metrics parameter
     *
     * @type {string}
     * @default 'universal_search'
     */
    @Prop() tisearch: string = 'universal_search';

    /**
     * Product model api url
     *
     * @type {string}
     * @default '/productmodel/{gpn}/orderables'
     */
    @Prop() productModelApiUrl: string = '/productmodel/{gpn}/orderables';

    /**
     * Search api url
     *
     * @type {string}
     * @default '/search/opn'
     */
    @Prop() searchApiUrl: string = '/search/opn';

    /**
    * Host URL
    *
    * @type {string}
    * @default ""
    */
    @Prop() tiHost: string = '';

    /**
     * Store host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com"
     */
    @Prop() tiStoreHost: string = 'https://www.ti.com';

    /**
     * Store CN Host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com.cn"
     */
    @Prop() tiStoreHostCn: string = 'https://www.ti.com.cn';

    /**
     * usecase for metrics
     *
     * @type {string}
     * @default "OPN-DETAILS"
     */
    @Prop() usecase: string = 'OPN-DETAILS';

    /**
     * add-to-cart metrics
     *
     * @type {string}
     * @default "ti.com-opndetailssearch"
     */
    @Prop() tiMetricsLocation: string = 'ti.com-opndetailssearch';

    /**
     * This locale is used for price formatting
     * it can be korean, german, english, chinese, japanese
     * while the main locale can be only english, chinese, japanese
     *
     * @type {string}
     * @default null
     */
    @Prop({ mutable: true }) currencyLocale: string = null;

    /**
     * Currency value
     *
     * @type {string}
     * @default null
     */
    @Prop({ mutable: true }) currency: string = null;

    /**
     * Value of locale
     *
     * @type {string}
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';
    @Watch('locale')
    handleLocaleChange() {
        this._updateLanguageStrings();
    }

    /**
     * OPN value
     *
     * @type {string}
     */
    @Prop() opn: string;
    @Watch('opn')
    handleOpnChange(newValue: string, oldValue: string) {
        if (!newValue || newValue.length === 0) {
            this.status = this._statusType.ERROR;
            return;
        }
        if (newValue !== oldValue) {
            this.status = this._statusType.LOADING;
        }
        this._updateData();
    }

    /**
     * GPN value
     *
     * @type {string}
     */
    @Prop() gpn: string;
    @Watch('gpn')
    handleGpnChange(newValue: string, oldValue: string) {
        if (!newValue || newValue.length === 0) {
            this.status = this._statusType.ERROR;
            return;
        }
        if (newValue !== oldValue) {
            this.status = this._statusType.LOADING;
        }
        this._updateData();
    }

    /**
     * OPN data for the given OPN
     * Should be an OPNDetail object, passed as a JSON string
     *
     * @type {string}
     */
    @Prop() opnDetailData: string;
    @Watch('opnDetailData')
    handleopnDetailDataChange(newValue: string, oldValue: string) {
        if (!newValue || newValue.length === 0) {
            this.status = this._statusType.ERROR;
            return;
        }
        if (newValue !== oldValue) {
            this.status = this._statusType.LOADING;
        }
        const opnDetail: OPNDetail = JSON.parse(newValue) as OPNDetail;
        this.updateData(opnDetail);
    }

    /**
    * Event emitted for metrics
    *
    * @type {EventEmitter}
    * @memberof TiOpnSnapshot
    */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    /**
     * Event for when component section has rendered
     */
    @Event() componentRendered: EventEmitter;

    /**
     * Event for when carrier icon/link clicked
     */
    @Event() tiOssCarrierClick: EventEmitter;

    /**
     * Event for when sku icon/link clicked
     */
    @Event() tiOssSkuClick: EventEmitter;

    /**
    * Lifecycle event handler.
    */
    async componentWillLoad() {
        // data
        if (this.opnDetailData && this.opnDetailData.length > 0) {
            const opnDetail: OPNDetail = JSON.parse(this.opnDetailData) as OPNDetail;
            this.updateData(opnDetail);
        } else if (this.opn && this.opn.length > 0 && this.gpn && this.gpn.length > 0) {
            this._updateData();
        }

        // language strings
        this._updateLanguageStrings();

        // currency
        const win = typeof (window as any) !== 'undefined' ? window : {} as Window;
        const com = (win as any).com;
        const userPref = (com && com.TI && com.TI.UserPreferences) ? com.TI.UserPreferences : {};
        if (this.currencyLocale == null) {
            this.currencyLocale = userPref.getUserLanguagePreference ? await userPref.getUserLanguagePreference() : 'en-US';
        }
        if (this.currency == null) {
            this.currency = userPref.getUserCurrencyPreference ? await userPref.getUserCurrencyPreference() : 'USD';
        }
        this._setCurrencyFormatter();
    }

    /**
    * Listen to the `currencyChange` event of header-currency-selection
    *
    * @param event
    */
    @Listen('tiCurrencyChange', { target: 'window' })
    async onCurrencyChange(event) {
        this.currency = event.detail;
    }

    /**
     * Listen to the `currencyReady` event of header-currency-selection
     *
     * @param event
     */
    @Listen('tiCurrencyReady', { target: 'window' })
    async onCurrencyReady(event) {
        this.currency = event.detail;
    }

    /**
     * Listen to languageChange event
     */
    @Listen('tiLanguageChange', { target: 'window' })
    onTiLanguageChange(event: CustomEvent) {
        this.currencyLocale = event.detail.locale;
    }

    /**
     * Listen to languageChange event
     */
    @Listen('tiLanguageReady', { target: 'window' })
    onTiLanguageReady(event: CustomEvent) {
        this.currencyLocale = event.detail.locale;
    }

    /**
     * Listen to the `tiCurrencyFormatReady` event from the header
     *
     */
    @Listen('tiCurrencyFormatReady', { target: 'window' })
    onCurrencyFormatReady() {
        this._setCurrencyFormatter();
    }

    /**
     * Listen to expand/collapse events of disclosure list and emits tiMetricsAction event for the same
     */
    @Listen('tiDisclosureListChange')
    onTiDisclosureListChange(event) {
        const metricsDetail = {
            eventName: 'chevron click',
            elementName: this._hostElement.tagName,
            data: {
                value: event.detail.action,
                column: 'qty_price'
            }
        };
        this.tiMetricsAction.emit(metricsDetail);
    }

    /**
    * Metrics interceptor for add-to-cart so we can emit from the source (OPN Snapshot)
    */
    @Listen('tiMetricsAction')
    onAddToCartTiMetricsAction(event: CustomEvent<TiMetricsActionEventDetail>) {
        if (event.detail.elementName === 'TI-ADD-TO-CART' && event.detail.eventName !== 'add to cart modal close') {
            event.stopPropagation();
            let newMetricsEvent = event.detail;
            newMetricsEvent.elementName = this._hostElement.tagName;
            this.tiMetricsAction.emit(newMetricsEvent);
        }
    }

    /**
     * Catch link clicks and attach analytics query parameters
     * @param event
     */
    @Listen('click')
    clickHandler(event: any) {
        let tag = (event.composedPath && event.composedPath()[0]) || event.target as HTMLElement;
        // chipshot click
        if (tag.className === 'ti-image-object') {
            tag = tag.parentElement.parentElement;
        }
        if (tag.tagName.toLowerCase() === 'a' && tag.getAttribute('href')) {
            event.preventDefault();
            event.stopPropagation(); //Fix to prevent multiple tabs from opening - Tarek - 1/24/2022
            const url = Util.formatUrl(
                tag.getAttribute('href'),
                {
                    searchTerm: this.opnDetails.opn,
                    tisearch: this.tisearch.toLocaleLowerCase(),
                    usecase: this.usecase
                },
                this.commonQueryString
            );
            if (tag.getAttribute('target') === '_blank') {
                window.open(url, '_blank');
            } else {
                window.location.href = url;
            }
        }
    }

    @Method()
    public async updateData(opnDetails: OPNDetail) {
        if (opnDetails && opnDetails.opn) {
            this.opnDetails = opnDetails;
            this.status = this._statusType.READY;
        }
        else {
            this.status = this._statusType.ERROR;
        }
    }

    private _setCurrencyFormatter(): void {
        // accessing the global utility function for formatting from window variable
        const win = typeof (window as any) !== 'undefined' ? window : {} as Window;
        const com = (win as any).com;
        if (com && com.TI && com.TI.CurrencyFormat) {
            this.currencyFormat = com.TI.CurrencyFormat;
        }
    }

    private async _updateData() {
        const config: OPNDaoConfig = OPNDao.config(this.tiHost, this.productModelApiUrl, this.searchApiUrl, this.customReelFeesApiUrl);
        this.updateData(await OPNDetailsDao.fetchOPNDetails(this.opn, this.gpn, this.locale, config));
    }

    /**
     * sets default locale as en-US for invalid locale and updates language strings.
     */
    private _updateLanguageStrings() {
        this.locale = Util.getValidLocale(this.locale);

        this.storeUrl = (languageStrings[this.locale]["storeUrl"]).replace(/{gpn}/, this.opnDetails.gpn).replace(/{opn}/, this.opnDetails.opn);
    }

    /**
     * Opens content dialog related to carrier options
     * @param opn - related opn
     * @param event - to handle keyboardevents 'enter' in addition to clicks
     */
    private _openCarrierDialog(opn: string, event?: KeyboardEvent) {
        if (!event || event.key === 'Enter') {
            this.tiOssCarrierClick.emit(opn);
        }
    }

    /**
     * Opens content dialog related to sku
     * @param opn - related opn
     * @param event - to handle keyboardevents 'enter' in addition to clicks
     */
    private _openSkuDialog(opn: string, event?: KeyboardEvent) {
        if (!event || event.key === 'Enter') {
            this.tiOssSkuClick.emit(opn);
        }
    }

    render() {
        if (this.status !== this._statusType.READY) {
            return '';
        }
        const opnParameters: OPNParameters = this.opnDetails.opnParameters;
        const basePrice: Price = opnParameters.basePriceList && opnParameters.basePriceList.find(currencyData => currencyData.currency === 'USD');
        const pins = opnParameters.pins === 0 || opnParameters.pins ? opnParameters.pins.toLocaleString(this.locale) : '—';
        const stockMode = (opnParameters.availableForPurchaseFlag !== 'Y' ? 'notAvailable' :
            Number(opnParameters.inventory) > 0 ? 'addToCart' : 'outOfStock');
        const loggedOut = opnParameters.loggedOut;
        return (
            <Host>
                <div class="ti-opn-details">
                    <div class="ti-opn-details-row">

                        <div class="ti-opn-details-info">
                            <ti-titled-description compact>
                                <div slot="title" class="ti-opn-details-part-info">
                                    {opnParameters.availableForPurchaseFlag === 'Y' ?
                                        <a href={`${this.tiHost}${this.storeUrl}`} class="ti-opn-details-part-number" >{opnParameters.orderablePartNumber}</a>
                                        :
                                        <span class="ti-opn-details-part-number">{opnParameters.orderablePartNumber}</span>
                                    }
                                    <ti-product-status compact status={opnParameters.marketingStatusId}
                                        tooltip={opnParameters.marketingStatusLongDesc}>
                                        <a data-navtitle="status-badge" href={languageStrings[this.locale]["productStatusLink"]}>
                                            {opnParameters.marketingStatusDescription}
                                        </a>
                                    </ti-product-status>
                                </div>
                            </ti-titled-description>

                            {opnParameters.relevantForCustomReel &&
                                <div class="ti-opn-details-custom-reel-lot-date">
                                    <ti-tooltip-trigger>
                                        <button
                                            class="ti-opn-details-tooltip-dialog-trigger"
                                            onClick={() => this._openCarrierDialog(opnParameters.orderablePartNumber)}
                                            onKeyDown={(ev) => this._openCarrierDialog(opnParameters.orderablePartNumber, ev)}
                                            tabindex="0">
                                            <ti-svg-icon icon-set="objects" size="s">custom-reels</ti-svg-icon>
                                            {languageStrings[this.locale]["customReelLabel"]}
                                        </button>
                                        <ti-tooltip position="auto">{languageStrings[this.locale]["customReelTooltip"]}</ti-tooltip>
                                    </ti-tooltip-trigger>
                                </div>
                            }
                            {opnParameters.relevantForLotDateCode &&
                                <div class="ti-opn-details-custom-reel-lot-date">
                                    <ti-tooltip-trigger>
                                        <button
                                            class="ti-opn-details-tooltip-dialog-trigger"
                                            onClick={() => this._openSkuDialog(opnParameters.orderablePartNumber)}
                                            onKeyDown={(ev) => this._openSkuDialog(opnParameters.orderablePartNumber, ev)}
                                            tabindex="0">
                                            <ti-svg-icon icon-set="objects" size="s">barcode</ti-svg-icon>
                                            {languageStrings[this.locale]["lotdateLabel"]}
                                        </button>
                                        <ti-tooltip position="auto">{languageStrings[this.locale]["lotdateTooltip"]}</ti-tooltip>
                                    </ti-tooltip-trigger>
                                </div>
                            }
                            {opnParameters.alternatePartNumbers && opnParameters.alternatePartNumbers.length ?
                                <div class="ti-opn-details-alternate-parts">
                                    <ti-tooltip-trigger>
                                        <span>{languageStrings[this.locale]["sameAs"] + ' ' + opnParameters.alternatePartNumbers.join(", ")}</span>
                                        <ti-tooltip>{languageStrings[this.locale]["sameAsToolTipContent"]}</ti-tooltip>
                                    </ti-tooltip-trigger>
                                </div>
                                :
                                null
                            }
                            {opnParameters.replacedBy ?
                                opnParameters.replacedByUrl ?
                                    <div>
                                        {languageStrings[this.locale]["replaceBy"] + ' '}
                                        <a href={opnParameters.replacedByUrl.replace(/#order-quality/, '')}> {opnParameters.replacedBy}</a>
                                    </div>
                                    :
                                    <div>
                                        {languageStrings[this.locale]["replaceBy"] + ' ' + opnParameters.replacedBy}
                                    </div>
                                : null
                            }
                            {opnParameters.hasReplacements &&
                                <div class="ti-opn-details-view-alternates">
                                    <ti-selection-tool-view-alternates
                                        gpn={this.opnDetails.gpn}
                                        locale={this.locale}
                                        part-number={this.opnDetails.opn}
                                        is-opn>
                                        <a target="_blank" class="ti_p-iconText">
                                            <span class="ti_p-iconText-icon">
                                                <ti-svg-icon appearance="secondary" size="s" icon-set="actions">open-in-new</ti-svg-icon>
                                            </span>
                                            {languageStrings[this.locale]['viewAlternatesLabel']}
                                        </a>
                                    </ti-selection-tool-view-alternates>
                                </div>
                            }
                            <ul class="ti-opn-details-info-list">
                                <li>
                                    <span class="ti-opn-details-info-list-label">{languageStrings[this.locale]["packagePins"]}: </span>
                                    {opnParameters.packageTypeUrl
                                        ?
                                        <span><a href={opnParameters.packageTypeUrl} target="_blank">{opnParameters.packaging}</a> | {pins}</span>
                                        :
                                        <span>{opnParameters.packaging + ' | ' + pins}</span>
                                    }
                                </li>
                                <li>
                                    <span class="ti-opn-details-info-list-label">{languageStrings[this.locale]["packageQuantityCarrier"]}: </span>
                                    {opnParameters.carrierUrl
                                        ?
                                        <span>
                                            <a href={opnParameters.carrierUrl} target="_blank">{(opnParameters.packageQuantity ? opnParameters.packageQuantity.toLocaleString(this.locale) : '—') + ' | ' + (opnParameters.packageCarrier ? opnParameters.packageCarrier : '—')}
                                            </a>
                                        </span>
                                        :
                                        <span>{(opnParameters.packageQuantity ? opnParameters.packageQuantity.toLocaleString(this.locale) : '—') + ' | ' + (opnParameters.packageCarrier ? opnParameters.packageCarrier : '—')}</span>
                                    }
                                </li>
                                <li>
                                    <span class="ti-opn-details-info-list-label">{languageStrings[this.locale]["operatingTemperature"]}: </span>
                                    {opnParameters.operatingTempRange}
                                </li>
                            </ul>
                        </div>

                        <div class="ti-opn-details-add-to-cart">
                            {loggedOut ?    // Show Login CTA
                                <div>
                                    <ti-button appearance="compact primary">
                                        <a href={`${this.tiHost}${this._loginUrl}${encodeURIComponent(window.location.href)}`}
                                            data-navtitle={languageStrings['en-US']["loginToOrder"]} data-lid={`indigo-${opnParameters.orderablePartNumber}`}>
                                            {languageStrings[this.locale]["loginToOrder"]}
                                        </a>
                                    </ti-button>
                                    <a class="ti-opn-details-availability" href={`${this.tiHost}${this._loginUrl}${encodeURIComponent(window.location.href)}`}
                                        data-navtitle={languageStrings['en-US']["loginToViewInventory"]} data-lid={`indigo-${opnParameters.orderablePartNumber}`}>
                                        <ti-svg-icon icon-set="objects" appearance="secondary" size="s">lock</ti-svg-icon>
                                        <span>{languageStrings[this.locale]["loginToViewInventory"]}</span>
                                    </a>
                                </div>
                                :       // Show Add To Cart / Availability Status
                                <div>
                                    {stockMode === 'notAvailable' ?    // "Not available"
                                        <span class="ti-opn-details-out-of-stock-modal">
                                            <div class="ti-opn-details-not-available-label">
                                                {languageStrings[this.locale]["notAvailable"]}
                                            </div>
                                        </span>
                                        : null
                                    }
                                    {stockMode === 'outOfStock' ?  // "Out of Stock"
                                        <div>
                                            <span class="ti-opn-details-out-of-stock-modal">
                                                <div class="ti-opn-details-out-of-stock-label">
                                                    {languageStrings[this.locale]["outOfStock"]}
                                                </div>
                                            </span>
                                            <ti-button appearance="link">
                                                <ti-notify-me locale={this.locale}
                                                    opn={opnParameters.orderablePartNumber}
                                                    gpn={this.opnDetails.gpn}>
                                                </ti-notify-me>
                                            </ti-button>
                                        </div>
                                        : null
                                    }
                                    {stockMode === 'addToCart' ?   // add-to-cart
                                        <div>
                                            <ti-add-to-cart
                                                data-lid="add_to_cart_modal"
                                                gpn={this.opnDetails.gpn}
                                                opn={opnParameters.orderablePartNumber}
                                                application-type="PF"
                                                locale={this.locale}
                                                currency-locale={this.currencyLocale}
                                                currency-exchange-rate-json={JSON.stringify(this.opnDetails.currencyExchangeRateMap)}
                                                description-api-url={`${this.tiHost}/popularorderables/addtocart/`}
                                                quantity-limit={opnParameters.inventory}
                                                product-status={OpnDetails._PRODUCT_STATUS_EN[opnParameters.marketingStatusId]}
                                                ti-store-url={this.tiStoreHost}
                                                ti-store-china-url={this.tiStoreHostCn}
                                                ti-metrics-location={this.tiMetricsLocation}
                                                use-custom-reel-api={opnParameters.customReelApi && opnParameters.relevantForCustomReel}
                                                relevant-for-custom-reel={`${opnParameters.relevantForCustomReel}`}
                                                relevant-for-lot-date-code={`${opnParameters.relevantForLotDateCode}`}
                                                package-quantity={opnParameters.packageQuantity}
                                                order-limit={opnParameters.orderLimit < 999999 ? opnParameters.orderLimit : false}
                                                custom-reel-fee={this.opnDetails.customReelPriceList}
                                                hide-carrier-sku-message="true"
                                                show-quantity-input
                                                hide-quantity-hint-text
                                                compact-button
                                                in-stickycolumn
                                                show-add-to-list>
                                            </ti-add-to-cart>
                                        </div>
                                        : null
                                    }
                                </div>
                            }
                            <div class="ti-opn-details-info-list">
                                {!loggedOut &&   // Show Inventory Details
                                    <div>
                                        {stockMode === 'addToCart' ?   // inventory
                                            <div class="ti-opn-details-info-list-item">
                                                <span class="ti-opn-details-info-list-label">{languageStrings[this.locale]["inventory"]}: </span>
                                                {Number.parseFloat(opnParameters.inventory).toLocaleString(this.locale)}
                                            </div>
                                            : null
                                        }
                                        {opnParameters.inventoryForecast && opnParameters.showFutureInventory && (stockMode === 'addToCart' || stockMode === 'outOfStock') ?    // forecast
                                            <div class="ti-opn-search-future-inventory ti-opn-details-info-list-item">
                                                <ti-inventory-forecast
                                                    opn={opnParameters.orderablePartNumber}
                                                    locale={this.locale}
                                                    next-availability-text={languageStrings[this.locale]["futureInventory"]}
                                                    data={JSON.stringify(opnParameters.inventoryForecast)}
                                                    expanded={stockMode === 'outOfStock'}
                                                    current-inventory={opnParameters.inventory}>
                                                </ti-inventory-forecast>
                                            </div>
                                            : null
                                        }
                                        {opnParameters.orderLimit < Math.min(Number(opnParameters.inventory), 999999) && stockMode === 'addToCart' &&   // limit
                                            <ti-tooltip-trigger class="ti-opn-details-info-list-item">
                                                {`${languageStrings[this.locale]["limit"]} ${opnParameters.orderLimit.toLocaleString()}`}
                                                <ti-tooltip position="bottom">{languageStrings[this.locale]["limitMessage"]}</ti-tooltip>
                                            </ti-tooltip-trigger>
                                        }
                                    </div>
                                }
                                {(stockMode === 'addToCart') || (stockMode === 'outOfStock') ?   // pricing
                                    <div class="ti-opn-details-info-list-qty ti-opn-details-info-list-item">
                                        <span class="ti-opn-details-info-list-label"> {languageStrings[this.locale]["quantityPrice"]} {` (${this.currency})`}: </span>
                                        {(opnParameters.tieredPriceList && opnParameters.tieredPriceList.length) && ((basePrice && basePrice.currencyPrice) && opnParameters.baseQuantity) ?
                                            <ti-disclosure-list>
                                                <span slot="label" style={{ 'white-space': 'nowrap' }}>{opnParameters.baseQuantity + ' | ' + this.currencyFormat.convertAndFormat(Number.parseFloat(basePrice.currencyPrice), this.opnDetails.currencyExchangeRateMap[this.currency] ? this.opnDetails.currencyExchangeRateMap[this.currency] : 1, true, this.currency, this.currencyLocale)}</span>
                                                {opnParameters.tieredPriceList.map(item => {
                                                    const price = item.priceList.find(currencyRecord => currencyRecord.currency === 'USD');
                                                    const isLastTier = opnParameters.tieredPriceList.indexOf(item) === opnParameters.tieredPriceList.length - 1;
                                                    const rangeFrom = Number.parseFloat(item.rangeFrom).toLocaleString(this.locale);
                                                    return (
                                                        <ti-disclosure-list-item>
                                                            <span>{isLastTier ? rangeFrom + '+' : rangeFrom + '-' + Number.parseFloat(item.rangeTo).toLocaleString(this.locale)}</span>
                                                            <span>{this.currencyFormat.convertAndFormat(Number.parseFloat(price.currencyPrice), this.opnDetails.currencyExchangeRateMap[this.currency] ? this.opnDetails.currencyExchangeRateMap[this.currency] : 1, true, this.currency, this.currencyLocale)}</span>
                                                        </ti-disclosure-list-item>
                                                    );
                                                })}
                                            </ti-disclosure-list>
                                            :
                                            (basePrice && opnParameters.baseQuantity) ?
                                                <span style={{ 'white-space': 'nowrap' }}>{opnParameters.baseQuantity + ' | ' + this.currencyFormat.convertAndFormat(Number.parseFloat(basePrice.currencyPrice), this.opnDetails.currencyExchangeRateMap[this.currency] ? this.opnDetails.currencyExchangeRateMap[this.currency] : 1, true, this.currency, this.currencyLocale)}</span>
                                                :
                                                <a href={languageStrings[this.locale]["contactTiLink"]}>
                                                    {languageStrings[this.locale]["contactTi"]}
                                                </a>
                                        }
                                    </div>
                                    : null
                                }
                            </div>
                        </div>

                    </div>
                </div>
            </Host>
        )
    }
}
